import { skipToken, useSuspenseQuery, type SuspenseQueryHookOptions } from '@apollo/client';
import moment, { type Moment } from 'moment-timezone';

import {
  stripeIntervalToMembershipIntervalLabel,
  stripeStatusToMembershipStatusLabel,
} from '../../constants';
import useCurrentUserV2 from '../useCurrentUserV2';
import { GET_VOLO_PASS_SUBSCRIPTION_DETAILS } from './gql/GET_USER_MEMBERSHIP_DETAILS';

type SuspenseOptions = SuspenseQueryHookOptions<typeof GET_VOLO_PASS_SUBSCRIPTION_DETAILS>;

const useUserVoloPassMembershipStatus = (suspenseOptions?: Omit<SuspenseOptions, 'variables'>) => {
  const { currentUser } = useCurrentUserV2();
  const {
    data: membershipData,
    refetch: membershipRefetch,
    error: membershipError,
  } = useSuspenseQuery(
    GET_VOLO_PASS_SUBSCRIPTION_DETAILS,
    currentUser && currentUser?._id
      ? {
          fetchPolicy: 'cache-and-network',
          ...suspenseOptions,
          variables: {
            userId: currentUser._id,
            type: 'VOLO_PASS',
          },
        }
      : skipToken
  );

  const { activeSubscription: activeVoloPassMembership, pastSubscription: pastVoloPassMembership } =
    membershipData || {};
  const activeMembership = activeVoloPassMembership?.[0];
  const pastMembership = pastVoloPassMembership?.[0];

  const userIsActiveVoloPassMember = Boolean(activeMembership);
  const userHasPastVoloPassMembership = Boolean(pastMembership);

  const hasMemberHistory = userIsActiveVoloPassMember || userHasPastVoloPassMembership;

  const {
    interval,
    status,
    start_date,
    end_date,
    organization,
    cancel_at,
    cancel_at_period_end,
    test_time,
  } = activeMembership || pastMembership || {};

  const now = moment(test_time || undefined);

  const voloPassMembershipStartDateMoment = start_date ? moment(start_date) : null;
  const voloPassMembershipEndDateMoment = end_date ? moment(end_date) : null;

  const oneMonthAfterPlanStarts = moment(voloPassMembershipStartDateMoment).add(1, 'months');
  const twoMonthsAfterPlanStarts = moment(voloPassMembershipStartDateMoment).add(2, 'months');

  let membershipExpirationDate: Moment;
  if (
    now.isSameOrAfter(voloPassMembershipStartDateMoment, 'day') &&
    now.isSameOrBefore(oneMonthAfterPlanStarts, 'day')
  ) {
    // first month
    membershipExpirationDate = moment(voloPassMembershipStartDateMoment).add(3, 'months');
  } else if (
    now.isSameOrAfter(oneMonthAfterPlanStarts, 'day') &&
    now.isSameOrBefore(twoMonthsAfterPlanStarts, 'day')
  ) {
    // second month
    membershipExpirationDate = moment(voloPassMembershipStartDateMoment).add(2, 'months');
  } else {
    // months 3+
    membershipExpirationDate = moment(voloPassMembershipEndDateMoment);
  }

  return {
    isVoloPassMember: userIsActiveVoloPassMember,
    hasVoloPassMemberHistory: hasMemberHistory,
    voloPassMembershipStatus: stripeStatusToMembershipStatusLabel(status),
    voloPassMembershipType: stripeIntervalToMembershipIntervalLabel(interval),
    voloPassMembershipStartDateMoment,
    voloPassMembershipEndDateMoment,
    voloPassMembershipOrganizationId: organization,
    voloPassMembershipRefetch: membershipRefetch,
    planCancelled: Boolean(cancel_at_period_end || cancel_at),
    /** can only be an instance of `ApolloError`if `errorPolicy: 'all'` is passed as options to hook */
    voloPassMembershipError: membershipError,
    cancellationDate: cancel_at || null,
    testTime: test_time || null,
    activeVoloPassMembership: activeMembership,
    membershipExpirationDate,
  };
};

export default useUserVoloPassMembershipStatus;
